import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/contexts/SidebarContext";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  LayoutDashboard,
  Store,
  Package,
  ShoppingCart,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  Bell,
  FileText,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LayoutGrid,
  GitCompare,
  Boxes,
  Link2,
  Copy,
  FolderTree,
  LucideIcon,
} from "lucide-react";

interface SubMenuItem {
  key: string;
  label: string;
  icon?: LucideIcon;
  path: string;
  placeholder?: boolean;
}

interface NavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  path: string;
  submenu?: SubMenuItem[];
  placeholder?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigationSections: NavSection[] = [
  {
    title: "Primary",
    items: [
      {
        key: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        path: "/",
      },
      { key: "sellers", label: "Sellers", icon: Store, path: "/sellers" },
      {
        key: "catalog",
        label: "Catalog",
        icon: Package,
        path: "/catalog",
        submenu: [
          {
            key: "catalog-overview",
            label: "Overview",
            icon: LayoutGrid,
            path: "/catalog",
          },
          {
            key: "catalog-sync",
            label: "Marketplace Sync",
            icon: RefreshCw,
            path: "/catalog/sync",
          },
          {
            key: "catalog-compare",
            label: "Compare Marketplaces",
            icon: GitCompare,
            path: "/catalog/compare",
          },
          {
            key: "catalog-master-skus",
            label: "Master SKUs",
            icon: Boxes,
            path: "/catalog/master-skus",
          },
          {
            key: "catalog-mapping",
            label: "Attribute Mapping",
            icon: Link2,
            path: "/catalog/mapping",
          },
          {
            key: "catalog-approval-jobs",
            label: "Approval Jobs",
            icon: RefreshCw,
            path: "/catalog/mapping/jobs",
          },
          {
            key: "catalog-mapping-review",
            label: "Mapping Review",
            icon: FolderTree,
            path: "/catalog/mapping-review",
          },
          {
            key: "catalog-duplicates",
            label: "Duplicates",
            icon: Copy,
            path: "/catalog/duplicates",
            placeholder: true,
          },
        ],
      },
      { key: "orders", label: "Orders", icon: ShoppingCart, path: "/orders" },
      {
        key: "performance",
        label: "Performance",
        icon: TrendingUp,
        path: "/performance",
      },
    ],
  },
  {
    title: "Operations",
    items: [
      {
        key: "disputes",
        label: "Disputes & Refunds",
        icon: AlertTriangle,
        path: "/disputes",
      },
      { key: "sync", label: "Sync & Logs", icon: RefreshCw, path: "/sync" },
      {
        key: "notifications",
        label: "Notifications",
        icon: Bell,
        path: "/notifications",
      },
    ],
  },
  {
    title: "System",
    items: [
      {
        key: "marketplaces",
        label: "Marketplaces",
        icon: Store,
        path: "/marketplaces",
      },
      { key: "audit", label: "Audit Log", icon: FileText, path: "/audit" },
      {
        key: "admin-users",
        label: "Admin Users",
        icon: Users,
        path: "/admin-users",
      },
      { key: "settings", label: "Settings", icon: Settings, path: "/settings" },
    ],
  },
];

interface SidebarProps {
  activePath?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ activePath = "/" }) => {
  const { collapsed, toggleSidebar } = useSidebar();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["catalog"]);

  const toggleSubmenu = (key: string) => {
    setExpandedMenus((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const isPathActive = (path: string, submenu?: SubMenuItem[]) => {
    if (submenu) {
      return submenu.some(
        (sub) =>
          activePath === sub.path || activePath.startsWith(sub.path + "/"),
      );
    }
    return activePath === path;
  };

  const renderNavItem = (
    item: NavItem,
    isSubmenuItem = false,
  ): React.ReactNode => {
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isExpanded = expandedMenus.includes(item.key);
    const isActive = isPathActive(item.path, item.submenu);
    const isExactActive = activePath === item.path;
    const Icon = item.icon;

    const navItemContent = (
      <div
        data-testid={`nav-${item.key}`}
        onClick={(e) => {
          if (hasSubmenu && !collapsed) {
            e.preventDefault();
            toggleSubmenu(item.key);
          }
        }}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer",
          "transition-colors duration-150",
          collapsed && "justify-center",
          isSubmenuItem && !collapsed && "pl-10",
          item.placeholder && "opacity-50",
          isExactActive
            ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
            : isActive && !isSubmenuItem
              ? "text-indigo-600 dark:text-indigo-400"
              : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200",
        )}
      >
        <Icon
          className={cn(
            "w-5 h-5 flex-shrink-0",
            isSubmenuItem && "w-4 h-4",
            isActive || isExactActive
              ? "text-indigo-600 dark:text-indigo-400"
              : "",
          )}
          strokeWidth={1.5}
        />
        {!collapsed && (
          <>
            <span
              className={cn(
                "text-sm font-medium truncate flex-1",
                isSubmenuItem && "text-[13px]",
              )}
            >
              {item.label}
            </span>
            {hasSubmenu && (
              <ChevronDown
                className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  isExpanded && "rotate-180",
                )}
              />
            )}
            {item.placeholder && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
                Soon
              </span>
            )}
          </>
        )}
      </div>
    );

    if (hasSubmenu) {
      return (
        <li key={item.key}>
          {collapsed ? (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <a href={item.submenu![0].path}>{navItemContent}</a>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="right"
                  sideOffset={8}
                  className={cn(
                    "py-2 rounded-md text-sm",
                    "bg-slate-900 dark:bg-slate-100",
                    "shadow-lg animate-fade-in z-50",
                  )}
                >
                  <div className="px-3 pb-1 font-semibold text-white dark:text-slate-900 border-b border-slate-700 dark:border-slate-300 mb-1">
                    {item.label}
                  </div>
                  {item.submenu!.map((sub) =>
                    sub.placeholder ? (
                      <button
                        key={sub.key}
                        type="button"
                        aria-disabled="true"
                        className={cn(
                          "block w-full text-left px-3 py-1.5 text-slate-300 dark:text-slate-600 hover:text-white dark:hover:text-slate-900",
                          activePath === sub.path &&
                            "text-indigo-400 dark:text-indigo-600 font-medium",
                          "opacity-50 cursor-not-allowed",
                        )}
                      >
                        {sub.label}
                      </button>
                    ) : (
                      <a
                        key={sub.key}
                        href={sub.path}
                        className={cn(
                          "block px-3 py-1.5 text-slate-300 dark:text-slate-600 hover:text-white dark:hover:text-slate-900",
                          activePath === sub.path &&
                            "text-indigo-400 dark:text-indigo-600 font-medium",
                        )}
                      >
                        {sub.label}
                      </a>
                    ),
                  )}
                  <Tooltip.Arrow className="fill-slate-900 dark:fill-slate-100" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          ) : (
            <>
              {navItemContent}
              {isExpanded && (
                <ul className="mt-1 space-y-0.5">
                  {item.submenu!.map((sub) => (
                    <li key={sub.key}>
                      {sub.placeholder ? (
                        <button
                          type="button"
                          aria-disabled="true"
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer pl-10 w-full text-left",
                            "transition-colors duration-150 text-sm font-medium",
                            "opacity-50 cursor-not-allowed",
                            activePath === sub.path
                              ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
                              : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200",
                          )}
                        >
                          {sub.icon && (
                            <sub.icon
                              className={cn(
                                "w-4 h-4 flex-shrink-0",
                                activePath === sub.path
                                  ? "text-indigo-600 dark:text-indigo-400"
                                  : "",
                              )}
                              strokeWidth={1.5}
                            />
                          )}
                          <span className="text-[13px] truncate flex-1">
                            {sub.label}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
                            Soon
                          </span>
                        </button>
                      ) : (
                        <a
                          href={sub.path}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer pl-10",
                            "transition-colors duration-150 text-sm font-medium",
                            activePath === sub.path
                              ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
                              : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200",
                          )}
                        >
                          {sub.icon && (
                            <sub.icon
                              className={cn(
                                "w-4 h-4 flex-shrink-0",
                                activePath === sub.path
                                  ? "text-indigo-600 dark:text-indigo-400"
                                  : "",
                              )}
                              strokeWidth={1.5}
                            />
                          )}
                          <span className="text-[13px] truncate flex-1">
                            {sub.label}
                          </span>
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </li>
      );
    }

    const wrappedItem = (
      <li key={item.key}>
        {item.placeholder ? (
          <button
            type="button"
            aria-disabled="true"
            className="w-full text-left"
          >
            {navItemContent}
          </button>
        ) : (
          <a href={item.path}>{navItemContent}</a>
        )}
      </li>
    );

    if (collapsed && !isSubmenuItem) {
      return (
        <Tooltip.Root key={item.key}>
          <Tooltip.Trigger asChild>{wrappedItem}</Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="right"
              sideOffset={8}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium",
                "bg-slate-900 dark:bg-slate-100",
                "text-white dark:text-slate-900",
                "shadow-lg animate-fade-in z-50",
              )}
            >
              {item.label}
              <Tooltip.Arrow className="fill-slate-900 dark:fill-slate-100" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      );
    }

    return wrappedItem;
  };

  return (
    <Tooltip.Provider delayDuration={100}>
      <aside
        data-testid="sidebar"
        className={cn(
          "fixed left-0 top-0 h-screen z-30",
          "bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800",
          "flex flex-col sidebar-transition",
          collapsed ? "w-16" : "w-64",
        )}
      >
        {/* Logo Section */}
        <div
          className={cn(
            "h-16 flex items-center border-b border-slate-200 dark:border-slate-800",
            collapsed ? "justify-center px-2" : "px-4",
          )}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
              <Store className="w-4 h-4 text-white" />
            </div>
            {!collapsed && (
              <span
                data-testid="sidebar-logo-text"
                className="font-heading font-bold text-lg text-slate-900 dark:text-slate-100"
              >
                SmartStore
              </span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {navigationSections.map((section, sectionIndex) => (
            <div key={section.title} className={cn(sectionIndex > 0 && "mt-6")}>
              {!collapsed && (
                <h3 className="px-4 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  {section.title}
                </h3>
              )}
              {collapsed && sectionIndex > 0 && (
                <div className="mx-3 mb-2 border-t border-slate-200 dark:border-slate-700" />
              )}
              <ul className="space-y-1 px-2">
                {section.items.map((item) => renderNavItem(item))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Collapse Toggle */}
        <div className="p-2 border-t border-slate-200 dark:border-slate-800">
          <button
            data-testid="sidebar-toggle-btn"
            onClick={toggleSidebar}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-md",
              "text-slate-500 dark:text-slate-400",
              "hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300",
              "transition-colors duration-150",
              collapsed && "justify-center",
            )}
          >
            {collapsed ? (
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm font-medium">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </Tooltip.Provider>
  );
};
